import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import Input from "../../../../components/Form/El/Input";
import { ApolloError, useMutation } from "@apollo/client";
import {
  CREATE_TIMEZONE,
  UPDATE_TIMEZONE,
} from "../../Services/Mutations/Mutaions";
import { DataCollectionItem } from "../../../../types/AdminDashboard";
import { isValidInteger } from "../../../../helpers/Validation";
import { getIntegerNumberFromString } from "../../../../helpers/utils";

const AdminDashboardTimeZone: FC<{
  currentId?: number;
  isNeedSave: boolean;
  afterSave: (id: number) => void;
  labelWidth: string;
  inputWidth: string;
  currentDataCollectionItem?: DataCollectionItem;
  setIsSaveLoading: (state: boolean) => void;
  setDisabledSaveButton: (state: boolean) => void;
  onErrorSave: (error: ApolloError) => void;
}> = ({
  labelWidth,
  currentDataCollectionItem,
  inputWidth,
  isNeedSave,
  currentId,
  afterSave,
  setIsSaveLoading,
  setDisabledSaveButton,
  onErrorSave,
}) => {
  const clearForm = useCallback(() => {
    setCurrentZone(null);
    setCurrentCode(null);
  }, []);

  const onAfterSave = useCallback((id: number) => {
    clearForm();
    afterSave(id || 0);
  }, []);

  const [updateItems] = useMutation(UPDATE_TIMEZONE, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { updateTimeZone } = data;

      if (!updateTimeZone) return;

      const { id } = updateTimeZone;

      onAfterSave(id || 0);
    },
  });
  const [createItems] = useMutation(CREATE_TIMEZONE, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { createTimeZone } = data;

      if (!createTimeZone) return;

      const { id } = createTimeZone;

      onAfterSave(id || 0);
    },
  });

  const [currentCode, setCurrentCode] = useState<string | null>(null);
  const [currentZone, setCurrentZone] = useState<string | null>(null);

  useEffect(() => {
    if (!currentDataCollectionItem) return;

    const { code, zone } = currentDataCollectionItem;

    if (typeof code === "number") {
      setCurrentCode(code.toString());
    }

    if (typeof zone === "number") {
      setCurrentZone(zone.toString());
    }
  }, [currentDataCollectionItem]);

  const isValidCode = useMemo(() => {
    return isValidInteger(currentCode);
  }, [currentCode]);

  const isValidZone = useMemo(() => {
    return isValidInteger(currentZone);
  }, [currentZone]);

  const onSave = useCallback(async () => {
    setIsSaveLoading(true);
    const handleSaveById = async () => {
      if (currentId) {
        return await updateItems({
          variables: {
            id: currentId,
            zone: getIntegerNumberFromString(currentZone),
            code: getIntegerNumberFromString(currentCode),
          },
        });
      }
      return await createItems({
        variables: {
          zone: getIntegerNumberFromString(currentZone),
          code: getIntegerNumberFromString(currentCode),
        },
      });
    };

    await handleSaveById();
  }, [currentId, currentZone, currentCode]);

  useEffect(() => {
    if (isNeedSave) {
      onSave();
    }
  }, [isNeedSave]);

  useEffect(() => {
    const isValidForm = isValidCode && isValidZone;
    setDisabledSaveButton(!isValidForm);
  }, [isValidCode, isValidZone]);

  return (
    <Fragment>
      <Input
        value={
          currentZone !== null &&
          typeof +currentZone === "number" &&
          !isNaN(+currentZone)
            ? currentZone
            : ""
        }
        onChange={(value) => {
          const stringValue = value.toString();
          setCurrentZone(stringValue ? stringValue : null);
        }}
        name="time-zone-zone"
        labelWidth={labelWidth}
        label="Zone"
        placeholder="Zone"
        inputWidth={inputWidth}
        type="number"
        isIncorrectValue={!isValidZone}
      />
      <Input
        value={
          currentCode !== null &&
          typeof +currentCode === "number" &&
          !isNaN(+currentCode)
            ? currentCode
            : ""
        }
        onChange={(value) => {
          const stringValue = value.toString();
          setCurrentCode(stringValue ? stringValue : null);
        }}
        name="time-zone-code"
        labelWidth={labelWidth}
        label="Code"
        placeholder="Code"
        inputWidth={inputWidth}
        type="number"
        isIncorrectValue={!isValidCode}
      />
    </Fragment>
  );
};

export default AdminDashboardTimeZone;
